// Settings › Tax › Cash rounding (ops-simulation T07).
//
// The tenant default rule for cash payments at the POS and the pre-fill for a
// sales invoice's 'Cash rounding'. A location can override it (Locations ›
// Settings). Card, UPI and wallet payments are always charged exactly.

import { useCallback, useEffect, useState } from 'react';
import { Button, Skeleton, useToast } from '@bbpos/ui';
import { resolveCashRoundingRule, type CashRoundingRule } from '@bbpos/utils';
import { tenantService } from '@/services/tenant.service';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import { SaveStatus } from '@/components/SaveStatus';
import { CashRoundingFields } from './CashRoundingFields';

interface CashRoundingCardProps {
  tenantId: string;
  currency: string;
}

const sameRule = (a: CashRoundingRule | null, b: CashRoundingRule) =>
  a !== null && a.step === b.step && a.mode === b.mode;

export function CashRoundingCard({ tenantId, currency }: CashRoundingCardProps) {
  const { toast } = useToast();
  const [saved, setSaved] = useState<CashRoundingRule | null>(null);
  const [rule, setRule] = useState<CashRoundingRule>(() => resolveCashRoundingRule(currency));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    tenantService.getById(tenantId).then((data) => {
      const row = data as unknown as { cash_rounding_step?: number | null; cash_rounding_mode?: string | null };
      const r = resolveCashRoundingRule(currency, { step: row.cash_rounding_step, mode: row.cash_rounding_mode });
      setSaved(r);
      setRule(r);
    }).catch(() => {
      toast({ title: 'Failed to load cash rounding', variant: 'error' });
    });
  }, [tenantId, currency, toast]);

  const isDirty = saved !== null && !sameRule(saved, rule);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const res = await tenantService.updateTaxSettings(tenantId, {
        cash_rounding_step: rule.step,
        cash_rounding_mode: rule.mode,
      });
      const stored = (res as unknown as { cash_rounding?: CashRoundingRule }).cash_rounding ?? rule;
      setSaved(stored);
      setRule(stored);
      toast({ title: 'Cash rounding updated', description: 'New bills follow it; past bills keep theirs.', variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to save', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [tenantId, rule, toast]);

  return (
    <div className={cn(GLASS_FORM, 'p-5 sm:p-6 mt-6 animate-slide-up')} data-testid="cash-rounding-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-success-500 flex items-center justify-center text-white font-bold shadow-md">
          ≈
        </div>
        <div>
          <h2 className="text-base font-semibold text-neutral-900 dark:text-white">Cash rounding</h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Rounds the total of cash payments to coins you use. Card, UPI and wallet are charged exactly.
          </p>
        </div>
      </div>
      {saved === null ? (
        <Skeleton variant="rect" height={96} className="rounded-xl" />
      ) : (
        <div className="space-y-4 max-w-xl">
          <CashRoundingFields currency={currency} value={rule} onChange={setRule} example={5237} disabled={isSaving} />
          <p className="text-[11px] text-neutral-400 dark:text-neutral-500">
            Used by every POS till and as the starting value on a sales invoice. A location can set its own rule under
            Locations › Settings.
          </p>
          <div className="flex items-center justify-end gap-3">
            <SaveStatus dirty={isDirty} />
            <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving || !isDirty}>
              {isSaving ? 'Saving...' : 'Save Cash Rounding'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
